import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BellRing, Check, CheckCheck, X, RefreshCw, ShoppingCart, QrCode, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { enablePush, getPushStatus, pushSupported } from '../../lib/push';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

const Orders = () => {
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('₹');
  const [updatingId, setUpdatingId] = useState(null);
  const [push, setPush] = useState({ supported: false, subscribed: false });
  const [enabling, setEnabling] = useState(false);
  const focusRef = useRef(null);

  useEffect(() => {
    fetchAll();
    getPushStatus().then(setPush).catch(() => {});
  }, []);

  // Scroll to the order opened from an alert / notification.
  useEffect(() => {
    if (!loading && focusId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, focusId]);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const [ordersRes, themeRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendor/theme`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
      ]);
      setOrders(ordersRes.data.orders || []);
      if (themeRes?.data?.theme?.currency_symbol) setCurrency(themeRes.data.theme.currency_symbol);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    setUpdatingId(orderId);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(`${API}/orders/${orderId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success(`Order marked ${status}`);
    } catch (error) {
      toast.error('Could not update order');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleEnablePush = async () => {
    setEnabling(true);
    try {
      await enablePush();
      setPush({ supported: true, subscribed: true, permission: 'granted' });
      toast.success('Order alerts enabled on this device 🔔');
    } catch (error) {
      toast.error(error.message || 'Could not enable alerts');
    } finally {
      setEnabling(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Orders</h2>
          <p className="text-muted-foreground mt-2">View and manage customer orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-orders-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          {pushSupported() && !push.subscribed && (
            <Button size="sm" onClick={handleEnablePush} disabled={enabling} data-testid="enable-alerts-btn">
              <BellRing className="h-4 w-4 mr-2" />
              {enabling ? 'Enabling…' : 'Enable order alerts'}
            </Button>
          )}
          {push.subscribed && (
            <Button size="sm" variant="outline" disabled data-testid="alerts-on-btn">
              <BellRing className="h-4 w-4 mr-2 text-green-600" /> Alerts on
            </Button>
          )}
        </div>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center text-center py-20">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <ShoppingCart className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-1">No orders yet</h3>
            <p className="text-muted-foreground max-w-sm mb-6">
              When a customer places an order from your store, it shows up here in real time. Share your store QR code or link to start receiving orders.
            </p>
            <a href="/dashboard/qr-code">
              <Button variant="outline"><QrCode className="h-4 w-4 mr-2" /> Get your store QR code</Button>
            </a>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const isFocus = order.id === focusId;
            const status = order.status || 'pending';
            return (
              <Card
                key={order.id}
                id={`order-${order.id}`}
                ref={isFocus ? focusRef : null}
                className={`transition-all ${isFocus ? 'ring-2 ring-purple-500 shadow-lg' : ''}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <CardTitle className="text-lg">Order #{order.id.slice(0, 8)}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <Badge variant="outline" className={`capitalize ${STATUS_STYLES[status] || ''}`}>
                        {status}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={order.fulfilment === 'scheduled'
                          ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                          : 'bg-emerald-100 text-emerald-800 border-emerald-200'}
                      >
                        🕒 {order.fulfilment === 'scheduled'
                          ? (order.scheduled_label || order.scheduled_for || 'Scheduled')
                          : 'ASAP'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Customer:</span>
                      <span className="text-sm font-medium">{order.customer_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Email:</span>
                      <span className="text-sm font-medium break-all">{order.customer_email}</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <p className="text-sm font-medium mb-2">Items:</p>
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{item.name} x {item.quantity}</span>
                          <span>{currency}{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                      <span>Total:</span>
                      <span>{currency}{order.total_amount.toFixed(2)}</span>
                    </div>

                    {/* Payment method + screenshot */}
                    {(order.payment_method || order.payment_screenshot_url) && (
                      <div className="border-t pt-2 mt-2 flex items-center justify-between gap-2">
                        {order.payment_method && (
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${order.payment_method === 'online' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {order.payment_method === 'online' ? '💳 Paid online' : '🏪 Pay at counter'}
                          </span>
                        )}
                        {order.payment_screenshot_url && (
                          <a
                            href={order.payment_screenshot_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900 underline underline-offset-2"
                          >
                            <ImageIcon className="h-3 w-3" /> View payment screenshot
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status actions */}
                  {(status === 'pending' || status === 'confirmed') && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                      {status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => updateStatus(order.id, 'confirmed')}
                          disabled={updatingId === order.id}
                          data-testid={`confirm-order-${order.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" /> Confirm
                        </Button>
                      )}
                      {status === 'confirmed' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateStatus(order.id, 'completed')}
                          disabled={updatingId === order.id}
                          data-testid={`complete-order-${order.id}`}
                        >
                          <CheckCheck className="h-4 w-4 mr-1" /> Mark completed
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => updateStatus(order.id, 'cancelled')}
                        disabled={updatingId === order.id}
                        data-testid={`cancel-order-${order.id}`}
                      >
                        <X className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
